// Two of someone's profiles side by side (Edward, Nov 2: "compare with your earlier one"), from
// whichever event they came: both on the map (the earlier one muted, a line to the later one),
// then what changed in freedom and clarity, the quadrant, the home domain, the top gifts and the
// theme to work on. Opens for whoever can open both results (signed in, this device, or a key).
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { Message, Page, Loading } from "../components/Page";
import { ResultMap, type MapRecord } from "../components/ResultMap";
import { formatDate } from "../components/ResultParts";
import { ApiError, api } from "../lib/api";
import { storedResultKey } from "../lib/resultKey";
import { isV4Result, type DomainCode, type ResultView, type ResultViewV4 } from "../lib/types";

type AnyResult = ResultView | ResultViewV4;

const mapRecordOf = (r: AnyResult): MapRecord =>
  isV4Result(r)
    ? {
        freedom: r.record.freedom,
        clarity: r.record.clarity,
        quadrant: r.record.quadrant,
        borderline: false,
        balanced: false,
        primary_domain: r.record.primary_domain,
        secondary_domain: r.record.primary_domain,
        domain_counts: r.record.domain_counts,
      }
    : r.record;

/** Both scoring versions name their domains in display.domains (a v1 result showed "REL" before: review, Nov 3). */
const domainNameOf = (r: AnyResult, code: DomainCode): string => r.display.domains.find((d) => d.code === code)?.name ?? code;

/** "+12", "−4" or "no change". */
const delta = (a: number, b: number) => (b === a ? "no change" : b > a ? `+${b - a}` : `−${a - b}`);

export function Compare() {
  const { a = "", b = "" } = useParams();
  const [pair, setPair] = useState<[AnyResult, AnyResult] | null>(null);
  const [error, setError] = useState<"no_access" | "failed" | null>(null);

  useEffect(() => {
    let live = true;
    // Each with its own results-email key, if this tab kept one (a key opens only its own take).
    Promise.all([api.getResult(a, storedResultKey(a)), api.getResult(b, storedResultKey(b))])
      .then(([ra, rb]) => {
        if (!live) return;
        // Earlier first, whatever order the address gave.
        setPair(Date.parse(ra.completedAt) <= Date.parse(rb.completedAt) ? [ra, rb] : [rb, ra]);
      })
      .catch((e) => live && setError(e instanceof ApiError && (e.status === 404 || e.status === 401 || e.status === 403) ? "no_access" : "failed"));
    return () => {
      live = false;
    };
  }, [a, b]);

  if (error === "no_access") {
    return (
      <Message title="Compare your profiles">
        <p>Sign in to compare your profiles. We'll email you a link.</p>
        <p className="mt-6 text-[15px]">
          <Link to="/sign-in">Sign in</Link>
        </p>
      </Message>
    );
  }
  if (error) return <Message title="Compare your profiles">We couldn't load these profiles. Reload the page to try again.</Message>;
  if (!pair) return <Loading label="Loading your profiles" />;

  const [before, after] = pair;
  // Two on the same day: the time tells them apart.
  const sameDay = formatDate(before.completedAt) === formatDate(after.completedAt);
  const when = (iso: string) => (sameDay ? `${formatDate(iso)}, ${new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : formatDate(iso));
  const recA = mapRecordOf(before);
  const recB = mapRecordOf(after);
  const v4 = isV4Result(before) && isV4Result(after) ? ([before, after] as const) : null;
  const topA = v4 ? v4[0].record.top_gifts : [];
  const topB = v4 ? v4[1].record.top_gifts : [];
  const domainNames = Object.fromEntries((["STR", "REL", "EXE", "INF"] as DomainCode[]).map((c) => [c, domainNameOf(after, c)])) as Record<DomainCode, string>;

  return (
    <Page width="wide">
      <p className="eyebrow mt-8 text-accent-text">My Gifts Profile</p>
      <h1 className="heading mt-2 text-[30px] leading-[1.1]">Compare your profiles</h1>
      <p className="mt-2 text-[15px] text-muted">
        {when(before.completedAt)} → {when(after.completedAt)}
      </p>
      {before.scoringVersion !== after.scoringVersion ? (
        <p className="mt-3 rounded-[12px] border border-line bg-soft p-3 text-pretty text-[14px] leading-relaxed" data-testid="versions-differ">
          These two were taken on different versions of the test, so some of the numbers don't line up exactly. Use them as a rough guide.
        </p>
      ) : null}

      <section className="mt-8 rounded-[16px] border border-line bg-panel p-5" aria-labelledby="compare-map">
        <h2 id="compare-map" className="heading text-[19px]">
          Where you are
        </h2>
        <p className="mt-1 text-[14px] text-muted">The earlier one is the muted dot; the line runs to the later one.</p>
        <div className="mt-4 flex justify-center">
          <ResultMap record={recB} domainNames={domainNames} before={{ freedom: recA.freedom, clarity: recA.clarity, quadrant: recA.quadrant }} />
        </div>
      </section>

      <section className="mt-6" aria-labelledby="compare-changes">
        <h2 id="compare-changes" className="heading text-[21px]">
          What changed
        </h2>
        <dl className="mt-3 divide-y divide-line overflow-hidden rounded-[16px] border border-line bg-panel">
          <Row label="Freedom" before={String(recA.freedom)} after={String(recB.freedom)} change={delta(recA.freedom, recB.freedom)} />
          <Row label="Clarity" before={String(recA.clarity)} after={String(recB.clarity)} change={delta(recA.clarity, recB.clarity)} />
          <Row label="Quadrant" before={recA.quadrant} after={recB.quadrant} change={recA.quadrant === recB.quadrant ? "same" : "moved"} />
          <Row
            label="Home domain"
            before={domainNameOf(before, recA.primary_domain)}
            after={domainNameOf(after, recB.primary_domain)}
            change={recA.primary_domain === recB.primary_domain ? "same" : "changed"}
          />
          {v4 ? (
            <Row
              label="Theme to work on"
              before={v4[0].display.theme.name}
              after={v4[1].display.theme.name}
              change={v4[0].record.work_on.primary_theme === v4[1].record.work_on.primary_theme ? "same" : "changed"}
            />
          ) : null}
        </dl>
      </section>

      {v4 ? (
        <section className="mt-6" aria-labelledby="compare-gifts">
          <h2 id="compare-gifts" className="heading text-[21px]">
            Top gifts
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <GiftList title={when(before.completedAt)} gifts={topA} other={topB} tag="not in your latest" />
            <GiftList title={when(after.completedAt)} gifts={topB} other={topA} tag="new" />
          </div>
        </section>
      ) : null}

      <p className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[15px]">
        <Link to={`/results/${before.takeId}`}>Open the {when(before.completedAt)} profile</Link>
        <Link to={`/results/${after.takeId}`}>Open the {when(after.completedAt)} profile</Link>
        <Link to="/me">All your profiles</Link>
      </p>
    </Page>
  );
}

function Row({ label, before, after, change }: { label: string; before: string; after: string; change: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5 px-4 py-3 sm:grid-cols-[10rem_minmax(0,1fr)_auto]">
      <dt className="text-[14px] font-semibold sm:self-center">{label}</dt>
      <dd className="col-span-2 row-start-2 text-[15px] sm:col-span-1 sm:row-start-auto">
        {before === after ? before : `${before} → ${after}`}
      </dd>
      <dd className="col-start-2 row-start-1 text-right text-[14px] text-muted sm:col-start-auto sm:row-start-auto sm:self-center">{change}</dd>
    </div>
  );
}

function GiftList({ title, gifts, other, tag }: { title: string; gifts: string[]; other: string[]; tag: string }) {
  return (
    <div className="rounded-[16px] border border-line bg-panel p-4">
      <p className="text-[14px] font-semibold text-muted">{title}</p>
      <ol className="mt-2 flex flex-col gap-1.5 text-[16px]">
        {gifts.map((g) => (
          <li key={g} className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{g}</span>
            {other.includes(g) ? null : <span className="rounded-full bg-soft px-2 py-0.5 text-[12px] text-muted">{tag}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}
